import { Button } from '@/components/ui/shadcn/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/shadcn/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/shadcn/select';
import { useServerFilters } from '@/hooks/use-server-filters';
import { useTranslations } from '@/hooks/useTranslations';
import AppLayout from '@/layouts/app-layout';
import { cn, useCan } from '@/lib/utils';
import { type PageProps } from '@/types';
import { Head, Link, router, useForm, usePage } from '@inertiajs/react';
import {
    BookOpen,
    BookOpenText,
    ChevronLeft,
    ChevronRight,
    Edit,
    ExternalLink,
    FileText,
    Globe2,
    LayoutGrid,
    List,
    Lock,
    MoreHorizontal,
    Plus,
    Search,
    SlidersHorizontal,
    Star,
    Trash2,
    X,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { route } from 'ziggy-js';

interface KnowledgeCategory {
    id: string;
    name: string;
    slug: string;
    description: string | null;
}

interface KnowledgeArticle {
    id: string;
    category: KnowledgeCategory | null;
    locale: string;
    title: string;
    slug: string;
    summary: string | null;
    status: string;
    tags: string[];
    is_restricted: boolean;
    is_featured: boolean;
    author?: { id: string; name: string } | null;
    updated_at?: string;
}

interface KnowledgeBaseIndexProps extends PageProps {
    articles: KnowledgeArticle[];
    categories: KnowledgeCategory[];
    category_counts: Record<string, number>;
    status_counts?: Record<string, number>;
    total_count: number;
    filters: {
        search?: string;
        locale?: string;
        category?: string;
        status?: string;
        sort?: string;
    };
    can_manage: boolean;
    locales: string[];
    [key: string]: unknown;
}

const CATEGORY_COLORS = [
    { bg: 'bg-info-subtle', icon: 'text-info-subtle-foreground' },
    { bg: 'bg-cat-purple', icon: 'text-cat-purple-foreground' },
    { bg: 'bg-success-subtle', icon: 'text-success-subtle-foreground' },
    { bg: 'bg-warning-subtle', icon: 'text-warning-subtle-foreground' },
    { bg: 'bg-cat-pink', icon: 'text-cat-pink-foreground' },
    { bg: 'bg-cat-indigo', icon: 'text-cat-indigo-foreground' },
];

const STATUS_STYLES: Record<string, string> = {
    published: 'bg-success-subtle text-success-subtle-foreground',
    draft: 'bg-warning-subtle text-warning-subtle-foreground',
    archived: 'bg-muted text-muted-foreground',
};

const STATUS_DOT: Record<string, string> = {
    published: 'bg-success',
    draft: 'bg-warning',
    archived: 'bg-muted-foreground',
};

function getInitials(name: string) {
    return name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();
}

function formatUpdatedAt(iso: string | undefined) {
    if (!iso) return { date: '—', time: '' };
    const d = new Date(iso);
    return {
        date: d.toLocaleDateString('en', { month: 'short', day: 'numeric', year: 'numeric' }),
        time: d.toLocaleTimeString('en', { hour: 'numeric', minute: '2-digit', hour12: true }),
    };
}

export default function KnowledgeBaseIndexPage() {
    const { props } = usePage<KnowledgeBaseIndexProps>();
    const {
        articles,
        categories,
        category_counts: categoryCounts,
        status_counts: statusCounts,
        total_count: totalCount,
        filters,
        locales,
        can_manage: canManage,
    } = props;
    const { can } = useCan();
    const { t } = useTranslations();
    const { applyFilters } = useServerFilters(filters, '/knowledge-base');

    const breadcrumbs = [{ title: t('knowledge_base.index.breadcrumbs.knowledge_base', 'Knowledge Base'), href: '/knowledge-base' }];

    // ── Search: client-side, dropdown only — NEVER triggers navigation ──
    const [search, setSearch] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [suggestionIdx, setSuggestionIdx] = useState(-1);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const searchWrapperRef = useRef<HTMLDivElement>(null);

    // Suggestions filtered from all loaded articles
    const suggestions = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (q.length < 2) return [];
        return articles
            .filter(
                (a) =>
                    a.title.toLowerCase().includes(q) ||
                    a.summary?.toLowerCase().includes(q) ||
                    a.category?.name.toLowerCase().includes(q) ||
                    a.tags.some((t) => t.toLowerCase().includes(q)),
            )
            .slice(0, 8);
    }, [articles, search]);

    useEffect(() => {
        setShowSuggestions(suggestions.length > 0);
        setSuggestionIdx(-1);
    }, [suggestions]);

    // Close on outside click
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (!searchWrapperRef.current?.contains(e.target as Node)) setShowSuggestions(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // ⌘K shortcut
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                searchInputRef.current?.focus();
            }
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, []);

    const navigateToArticle = (article: KnowledgeArticle) => {
        setSearch('');
        setShowSuggestions(false);
        router.visit(route('knowledge-base.show', { slug: article.slug, locale: article.locale }));
    };

    const highlightMatch = (text: string, q: string) => {
        const idx = text.toLowerCase().indexOf(q.toLowerCase());
        if (idx === -1 || !q) return <span>{text}</span>;
        return (
            <span>
                {text.slice(0, idx)}
                <mark className="bg-primary/15 text-primary rounded-sm px-0.5 not-italic">{text.slice(idx, idx + q.length)}</mark>
                {text.slice(idx + q.length)}
            </span>
        );
    };

    // ── Category navigation ─────────────────────────────────────────────
    const selectedCategorySlug = filters.category ?? '';
    const selectedLocale = filters.locale ?? 'lt';
    const selectedSort = filters.sort ?? 'updated';
    const selectedStatus = filters.status ?? '';
    const activeCategory = categories.find((c) => c.slug === selectedCategorySlug);

    // isHome: only false when a category is selected
    const isHome = !selectedCategorySlug;

    const goHome = () => applyFilters({ category: undefined, status: undefined, search: undefined });

    const selectCategory = (slug: string) => {
        setAdminMode(false);
        applyFilters({ category: slug, status: undefined });
    };

    // ── Category management ─────────────────────────────────────────────
    const [addingCategory, setAddingCategory] = useState(false);
    const categoryForm = useForm({ name: '' });

    const handleAddCategory = (e: React.FormEvent) => {
        e.preventDefault();
        categoryForm.post(route('knowledge-base.categories.store'), {
            onSuccess: () => {
                categoryForm.reset();
                setAddingCategory(false);
            },
        });
    };

    const handleDeleteCategory = (id: string) => {
        if (!confirm(t('knowledge_base.index.categories.confirm_delete', 'Delete this category?'))) return;
        router.delete(route('knowledge-base.categories.destroy', id));
    };

    const handleDeleteArticle = (id: string) => {
        if (!confirm(t('knowledge_base.index.articles.confirm_delete', 'Delete this article permanently?'))) return;
        router.delete(route('knowledge-base.destroy', id));
    };

    // ── Admin table toggle (managers only) ──────────────────────────────
    const [adminMode, setAdminMode] = useState(false);
    const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
    const [rowsPerPage, setRowsPerPage] = useState(8);
    const [currentPage, setCurrentPage] = useState(1);
    useEffect(() => setCurrentPage(1), [articles]);

    const totalPages = Math.max(1, Math.ceil(articles.length / rowsPerPage));
    const clampedPage = Math.min(currentPage, totalPages);
    const paginatedArticles = articles.slice((clampedPage - 1) * rowsPerPage, clampedPage * rowsPerPage);
    const startRow = articles.length === 0 ? 0 : (clampedPage - 1) * rowsPerPage + 1;
    const endRow = Math.min(clampedPage * rowsPerPage, articles.length);

    // ── Portal derived data ─────────────────────────────────────────────
    const featuredArticle = articles.find((a) => a.is_featured) ?? (articles.length > 0 ? articles[0] : null);
    const quickLinks = articles.filter((a) => a !== featuredArticle).slice(0, 4);
    const getCategoryCount = (id: string) => categoryCounts[id] ?? 0;
    const noCategoryCount = categoryCounts['__no_category__'] ?? 0;

    const STATUS_TABS = [
        { key: '', label: t('knowledge_base.index.status_tabs.all', 'All') },
        { key: 'published', label: t('knowledge_base.status.published', 'Published') },
        { key: 'draft', label: t('knowledge_base.status.draft', 'Drafts') },
        { key: 'archived', label: t('knowledge_base.status.archived', 'Archived') },
    ];

    return (
        <AppLayout breadcrumbs={breadcrumbs}>
            <Head title={t('knowledge_base.index.title', 'Knowledge Base')} />

            <div className="flex flex-1 flex-col overflow-hidden" style={{ minHeight: 0 }}>
                {/* ══ HERO — always visible, always centred ══════════════ */}
                <div className="shrink-0 border-b px-6 py-10 text-center">
                    <h1 className="text-3xl font-bold tracking-tight">{t('knowledge_base.index.heading', 'Knowledge Base')}</h1>
                    <p className="text-muted-foreground mt-2 text-base">
                        {t('knowledge_base.index.subheading', 'Internal procedures, onboarding notes, and team documentation.')}
                    </p>

                    {/* Search — dropdown only, no navigation */}
                    <div className="mx-auto mt-6 max-w-xl">
                        <div ref={searchWrapperRef} className="relative">
                            <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3.5 z-10 h-4 w-4 -translate-y-1/2" />
                            <input
                                ref={searchInputRef}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Escape') {
                                        setShowSuggestions(false);
                                        setSuggestionIdx(-1);
                                    } else if (e.key === 'ArrowDown') {
                                        e.preventDefault();
                                        setSuggestionIdx((i) => Math.min(i + 1, suggestions.length - 1));
                                    } else if (e.key === 'ArrowUp') {
                                        e.preventDefault();
                                        setSuggestionIdx((i) => Math.max(i - 1, -1));
                                    } else if (e.key === 'Enter' && suggestionIdx >= 0) {
                                        e.preventDefault();
                                        navigateToArticle(suggestions[suggestionIdx]);
                                    } else if (e.key === 'Enter' && suggestionIdx < 0 && search.trim()) {
                                        // Enter with no selection → just keep the dropdown open, do not navigate
                                        e.preventDefault();
                                    }
                                }}
                                placeholder={t('knowledge_base.index.search_placeholder', 'Search guides, articles, and documents...')}
                                className="border-input bg-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-11 w-full rounded-md border px-3 py-2 pr-10 pl-10 text-sm transition-colors focus-visible:ring-1 focus-visible:outline-none"
                                autoComplete="off"
                            />
                            {search ? (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSearch('');
                                        setShowSuggestions(false);
                                        searchInputRef.current?.focus();
                                    }}
                                    className="text-muted-foreground hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            ) : (
                                <div className="pointer-events-none absolute top-1/2 right-3.5 -translate-y-1/2">
                                    <kbd className="bg-muted text-muted-foreground rounded border px-1.5 py-0.5 font-mono text-[11px]">⌘K</kbd>
                                </div>
                            )}

                            {/* ── Suggestions dropdown ── */}
                            {showSuggestions && (
                                <div className="bg-popover absolute top-full right-0 left-0 z-50 mt-1 overflow-hidden rounded-xl border text-left shadow-md">
                                    {suggestions.map((article, i) => (
                                        <button
                                            key={article.id}
                                            type="button"
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                                navigateToArticle(article);
                                            }}
                                            className={cn(
                                                'hover:bg-accent flex w-full items-start gap-3 rounded-md px-4 py-3 transition-colors',
                                                i === suggestionIdx && 'bg-accent',
                                                i > 0 && 'border-t',
                                            )}
                                        >
                                            <div className="bg-muted mt-0.5 shrink-0 rounded-md p-1.5">
                                                <FileText className="text-muted-foreground h-3.5 w-3.5" />
                                            </div>
                                            <div className="min-w-0 flex-1 text-left">
                                                <div className="text-sm leading-snug font-medium">{highlightMatch(article.title, search.trim())}</div>
                                                <div className="text-muted-foreground mt-0.5 flex items-center gap-1.5 text-xs">
                                                    {article.category && <span>{article.category.name}</span>}
                                                    {article.category && article.tags.length > 0 && <span>·</span>}
                                                    {article.tags.slice(0, 2).map((tag) => (
                                                        <span key={tag}>#{tag}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                    {suggestions.length === 0 && (
                                        <div className="text-muted-foreground px-4 py-6 text-center text-sm">
                                            {t('knowledge_base.index.search_no_results', 'No articles found')}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* ══ BODY: sidebar + main ════════════════════════════════ */}
                <div className="flex flex-1 overflow-hidden">
                    {/* ── Left sidebar (categories only, no search) ──── */}
                    <aside className="bg-sidebar hidden w-56 shrink-0 flex-col border-r lg:flex">
                        <nav className="flex-1 overflow-y-auto p-2">
                            <p className="text-muted-foreground mb-1 px-2 py-1 text-[11px] font-medium tracking-wider uppercase">
                                {t('knowledge_base.index.categories.label', 'Categories')}
                            </p>

                            {/* All topics */}
                            <button
                                type="button"
                                onClick={goHome}
                                className={cn(
                                    'flex w-full items-center justify-between rounded-md px-2 py-1.5 text-sm transition-colors',
                                    isHome
                                        ? 'bg-accent text-accent-foreground font-medium'
                                        : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                                )}
                            >
                                <span className="flex items-center gap-2 truncate">
                                    <BookOpenText className="h-3.5 w-3.5 shrink-0" />
                                    {t('knowledge_base.index.all_articles', 'All topics')}
                                </span>
                                <span
                                    className={cn('ml-1 shrink-0 rounded px-1.5 py-0.5 text-[11px]', isHome ? 'bg-accent-foreground/10' : 'bg-muted')}
                                >
                                    {totalCount}
                                </span>
                            </button>

                            {/* Categories */}
                            {categories.map((category, i) => {
                                const isActive = selectedCategorySlug === category.slug;
                                const colors = CATEGORY_COLORS[i % CATEGORY_COLORS.length];
                                return (
                                    <div key={category.id} className="group/cat relative">
                                        <button
                                            type="button"
                                            onClick={() => selectCategory(category.slug)}
                                            className={cn(
                                                'flex w-full items-center justify-between rounded-md px-2 py-1.5 text-sm transition-colors',
                                                isActive
                                                    ? 'bg-accent text-accent-foreground font-medium'
                                                    : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                                            )}
                                        >
                                            <span className="flex items-center gap-2 truncate">
                                                <div className={cn('shrink-0 rounded p-0.5', colors.bg)}>
                                                    <BookOpen className={cn('h-3 w-3', colors.icon)} />
                                                </div>
                                                <span className="truncate">{category.name}</span>
                                            </span>
                                            <div className="ml-1 flex shrink-0 items-center gap-1">
                                                <span
                                                    className={cn(
                                                        'rounded px-1.5 py-0.5 text-[11px]',
                                                        isActive ? 'bg-accent-foreground/10' : 'bg-muted',
                                                    )}
                                                >
                                                    {getCategoryCount(category.id)}
                                                </span>
                                                {canManage && (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteCategory(category.id);
                                                        }}
                                                        className="text-destructive hidden rounded p-0.5 group-hover/cat:flex"
                                                    >
                                                        <X className="h-3 w-3" />
                                                    </button>
                                                )}
                                            </div>
                                        </button>
                                    </div>
                                );
                            })}

                            {/* No category */}
                            {noCategoryCount > 0 && (
                                <button
                                    type="button"
                                    onClick={() => selectCategory('__no_category__')}
                                    className={cn(
                                        'flex w-full items-center justify-between rounded-md px-2 py-1.5 text-sm transition-colors',
                                        selectedCategorySlug === '__no_category__'
                                            ? 'bg-accent text-accent-foreground font-medium'
                                            : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                                    )}
                                >
                                    <span className="flex items-center gap-2 truncate">
                                        <div className="bg-muted shrink-0 rounded p-0.5">
                                            <FileText className="text-muted-foreground h-3 w-3" />
                                        </div>
                                        <span className="truncate">{t('knowledge_base.index.no_category', 'No category')}</span>
                                    </span>
                                    <span
                                        className={cn(
                                            'ml-1 shrink-0 rounded px-1.5 py-0.5 text-[11px]',
                                            selectedCategorySlug === '__no_category__' ? 'bg-accent-foreground/10' : 'bg-muted',
                                        )}
                                    >
                                        {noCategoryCount}
                                    </span>
                                </button>
                            )}

                            {/* Add category */}
                            {canManage && (
                                <div className="mt-2">
                                    {!addingCategory ? (
                                        <button
                                            type="button"
                                            onClick={() => setAddingCategory(true)}
                                            className="text-muted-foreground hover:text-foreground flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-sm transition-colors"
                                        >
                                            <Plus className="h-3.5 w-3.5" />
                                            {t('knowledge_base.index.categories.add', 'Add category')}
                                        </button>
                                    ) : (
                                        <form onSubmit={handleAddCategory} className="space-y-1.5 px-1 pt-1">
                                            <input
                                                autoFocus
                                                value={categoryForm.data.name}
                                                onChange={(e) => categoryForm.setData('name', e.target.value)}
                                                placeholder={t('knowledge_base.index.categories.name_placeholder', 'Category name')}
                                                className="border-input bg-background h-7 w-full rounded-md border px-2 text-xs focus:ring-1 focus:outline-none"
                                            />
                                            <div className="flex gap-1">
                                                <button
                                                    type="submit"
                                                    disabled={categoryForm.processing || !categoryForm.data.name.trim()}
                                                    className="text-primary flex items-center gap-1 rounded px-2 py-1 text-xs font-medium disabled:opacity-50"
                                                >
                                                    <Plus className="h-3 w-3" />
                                                    {t('common.save', 'Save')}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setAddingCategory(false);
                                                        categoryForm.reset();
                                                    }}
                                                    className="text-muted-foreground rounded px-2 py-1 text-xs"
                                                >
                                                    {t('common.cancel', 'Cancel')}
                                                </button>
                                            </div>
                                        </form>
                                    )}
                                </div>
                            )}
                        </nav>

                        {/* Sidebar footer */}
                        <div className="space-y-2 border-t p-3">
                            {can('knowledge-base.create') && (
                                <Button asChild size="sm" className="w-full">
                                    <Link href={route('knowledge-base.create')}>
                                        <Plus className="h-4 w-4" />
                                        {t('knowledge_base.index.actions.create', 'New Article')}
                                    </Link>
                                </Button>
                            )}
                            <Select value={selectedLocale} onValueChange={(v) => applyFilters({ locale: v })}>
                                <SelectTrigger className="h-8 w-full text-xs">
                                    <Globe2 className="h-3.5 w-3.5" />
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {locales.map((l) => (
                                        <SelectItem key={l} value={l}>
                                            {l.toUpperCase()}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {can('knowledge-base.update') && (
                                <Select value={filters.status || 'all'} onValueChange={(v) => applyFilters({ status: v === 'all' ? undefined : v })}>
                                    <SelectTrigger className="h-8 w-full text-xs">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">{t('knowledge_base.index.status_tabs.all', 'All statuses')}</SelectItem>
                                        <SelectItem value="published">{t('knowledge_base.status.published', 'Published')}</SelectItem>
                                        <SelectItem value="draft">{t('knowledge_base.status.draft', 'Draft')}</SelectItem>
                                        <SelectItem value="archived">{t('knowledge_base.status.archived', 'Archived')}</SelectItem>
                                    </SelectContent>
                                </Select>
                            )}
                        </div>
                    </aside>

                    {/* ── Main content ─────────────────────────────────── */}
                    <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
                        {isHome ? (
                            /* ─── Portal home content ───────────────────── */
                            <div className="flex-1 overflow-y-auto">
                                <div className="mx-auto max-w-4xl space-y-8 px-6 py-6">
                                    {/* Category cards */}
                                    {(categories.length > 0 || noCategoryCount > 0) && (
                                        <section>
                                            <h2 className="text-muted-foreground mb-4 text-[11px] font-medium tracking-wider uppercase">
                                                {t('knowledge_base.index.categories.label', 'Categories')}
                                            </h2>
                                            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
                                                {categories.map((cat, i) => {
                                                    const colors = CATEGORY_COLORS[i % CATEGORY_COLORS.length];
                                                    return (
                                                        <button
                                                            key={cat.id}
                                                            type="button"
                                                            onClick={() => selectCategory(cat.slug)}
                                                            className="bg-card hover:bg-accent/30 rounded-xl border p-4 text-left transition-colors"
                                                        >
                                                            <div className={cn('mb-3 inline-flex rounded-lg p-2.5', colors.bg)}>
                                                                <BookOpen className={cn('h-5 w-5', colors.icon)} />
                                                            </div>
                                                            <div className="text-sm font-medium">{cat.name}</div>
                                                            {cat.description && (
                                                                <div className="text-muted-foreground mt-1 line-clamp-2 text-xs">
                                                                    {cat.description}
                                                                </div>
                                                            )}
                                                            <div className="text-muted-foreground mt-3 text-xs">
                                                                {t('knowledge_base.index.categories.article_count', ':count articles').replace(
                                                                    ':count',
                                                                    String(getCategoryCount(cat.id)),
                                                                )}
                                                            </div>
                                                        </button>
                                                    );
                                                })}
                                                {noCategoryCount > 0 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => selectCategory('__no_category__')}
                                                        className="bg-card hover:bg-accent/30 rounded-xl border p-4 text-left transition-colors"
                                                    >
                                                        <div className="bg-muted mb-3 inline-flex rounded-lg p-2.5">
                                                            <FileText className="text-muted-foreground h-5 w-5" />
                                                        </div>
                                                        <div className="text-sm font-medium">
                                                            {t('knowledge_base.index.no_category', 'No category')}
                                                        </div>
                                                        <div className="text-muted-foreground mt-3 text-xs">
                                                            {t('knowledge_base.index.categories.article_count', ':count articles').replace(
                                                                ':count',
                                                                String(noCategoryCount),
                                                            )}
                                                        </div>
                                                    </button>
                                                )}
                                            </div>
                                        </section>
                                    )}

                                    {/* Featured article */}
                                    {featuredArticle && (
                                        <Link
                                            href={route('knowledge-base.show', { slug: featuredArticle.slug, locale: featuredArticle.locale })}
                                            className="group border-primary/20 bg-primary/5 hover:bg-primary/10 relative flex flex-col justify-between overflow-hidden rounded-xl border p-6 transition-colors sm:flex-row sm:items-center sm:gap-6"
                                        >
                                            <div className="min-w-0 flex-1">
                                                <span className="text-primary mb-2 inline-flex items-center gap-1.5 text-xs font-semibold tracking-wider uppercase">
                                                    {featuredArticle.is_featured && <Star className="h-3 w-3 fill-current" />}
                                                    {t('knowledge_base.index.featured.label', 'Start here')}
                                                </span>
                                                <h3 className="text-lg leading-snug font-semibold">{featuredArticle.title}</h3>
                                                {featuredArticle.summary && (
                                                    <p className="text-muted-foreground mt-1.5 line-clamp-2 text-sm">{featuredArticle.summary}</p>
                                                )}
                                                <Button size="sm" className="pointer-events-none mt-4">
                                                    {t('knowledge_base.index.featured.open', 'Open guide')}
                                                </Button>
                                            </div>
                                            <BookOpenText className="text-primary/20 mt-4 h-20 w-20 shrink-0 sm:mt-0" />
                                        </Link>
                                    )}

                                    {/* Quick links */}
                                    {quickLinks.length > 0 && (
                                        <section>
                                            <h2 className="font-semibold">{t('knowledge_base.index.quick_links.title', 'Quick links')}</h2>
                                            <p className="text-muted-foreground mt-0.5 text-sm">
                                                {t('knowledge_base.index.quick_links.subtitle', 'Frequently used documents and resources.')}
                                            </p>
                                            <div className="mt-4 grid grid-cols-2 gap-3 xl:grid-cols-4">
                                                {quickLinks.map((article) => (
                                                    <Link
                                                        key={article.id}
                                                        href={route('knowledge-base.show', { slug: article.slug, locale: article.locale })}
                                                        className="bg-card group hover:bg-accent/30 flex flex-col justify-between gap-3 rounded-xl border p-4 transition-colors"
                                                    >
                                                        <div>
                                                            <div className="bg-muted mb-2.5 inline-flex rounded-lg p-2">
                                                                <FileText className="text-muted-foreground h-4 w-4" />
                                                            </div>
                                                            <div className="text-sm leading-snug font-medium">{article.title}</div>
                                                            {article.summary && (
                                                                <div className="text-muted-foreground mt-1 line-clamp-2 text-xs">
                                                                    {article.summary}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <ExternalLink className="text-muted-foreground h-3.5 w-3.5 self-end" />
                                                    </Link>
                                                ))}
                                            </div>
                                        </section>
                                    )}

                                    {/* All articles */}
                                    {articles.length > 0 && (
                                        <section>
                                            <div className="mb-4 flex items-center justify-between">
                                                <h2 className="font-semibold">{t('knowledge_base.index.all_articles_section', 'All articles')}</h2>
                                                {canManage && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setAdminMode((v) => !v)}
                                                        className={cn(
                                                            'flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs transition-colors',
                                                            adminMode ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:bg-accent',
                                                        )}
                                                    >
                                                        <SlidersHorizontal className="h-3.5 w-3.5" />
                                                        {t('knowledge_base.index.admin_view', 'Manage')}
                                                    </button>
                                                )}
                                            </div>
                                            {canManage && adminMode ? (
                                                <>
                                                    <div className="mb-3 border-b pb-3">
                                                        <div className="flex items-center gap-2">
                                                            {STATUS_TABS.map((tab) => (
                                                                <button
                                                                    key={tab.key}
                                                                    type="button"
                                                                    onClick={() => applyFilters({ status: tab.key || undefined })}
                                                                    className={cn(
                                                                        'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                                                                        selectedStatus === tab.key
                                                                            ? 'bg-accent text-accent-foreground'
                                                                            : 'text-muted-foreground hover:bg-accent',
                                                                    )}
                                                                >
                                                                    {tab.label}
                                                                    {statusCounts?.[tab.key] ? ` (${statusCounts[tab.key]})` : ''}
                                                                </button>
                                                            ))}
                                                            <div className="ml-auto">
                                                                <Select value={selectedSort} onValueChange={(v) => applyFilters({ sort: v })}>
                                                                    <SelectTrigger className="h-7 w-44 text-xs">
                                                                        <SelectValue />
                                                                    </SelectTrigger>
                                                                    <SelectContent>
                                                                        <SelectItem value="updated">
                                                                            {t('knowledge_base.index.filters.sort_newest', 'Newest updated')}
                                                                        </SelectItem>
                                                                        <SelectItem value="oldest">
                                                                            {t('knowledge_base.index.filters.sort_oldest', 'Oldest')}
                                                                        </SelectItem>
                                                                        <SelectItem value="title">
                                                                            {t('knowledge_base.index.filters.sort_title', 'Title A–Z')}
                                                                        </SelectItem>
                                                                    </SelectContent>
                                                                </Select>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="overflow-hidden rounded-lg border">
                                                        <table className="w-full border-separate border-spacing-0 text-sm">
                                                            <thead>
                                                                <tr className="bg-muted/30 [&>th]:border-b">
                                                                    <th className="text-muted-foreground px-4 py-3 text-left text-xs font-medium">
                                                                        {t('knowledge_base.index.table.title', 'Title')}
                                                                    </th>
                                                                    <th className="text-muted-foreground px-4 py-3 text-left text-xs font-medium">
                                                                        {t('knowledge_base.index.table.status', 'Status')}
                                                                    </th>
                                                                    <th className="text-muted-foreground hidden px-4 py-3 text-left text-xs font-medium sm:table-cell">
                                                                        {t('knowledge_base.index.table.updated', 'Updated')}
                                                                    </th>
                                                                    <th className="w-10 px-4 py-3" />
                                                                </tr>
                                                            </thead>
                                                            <tbody className="divide-y">
                                                                {paginatedArticles.map((article) => {
                                                                    const { date, time } = formatUpdatedAt(article.updated_at);
                                                                    return (
                                                                        <tr
                                                                            key={article.id}
                                                                            className="group hover:bg-muted/20 transition-colors [&>td:first-child]:rounded-l-md [&>td:last-child]:rounded-r-md"
                                                                        >
                                                                            <td className="px-4 py-3">
                                                                                <Link
                                                                                    href={route('knowledge-base.show', {
                                                                                        slug: article.slug,
                                                                                        locale: article.locale,
                                                                                    })}
                                                                                    className="flex items-start gap-3"
                                                                                >
                                                                                    <div className="bg-muted mt-0.5 shrink-0 rounded p-1.5">
                                                                                        <FileText className="text-muted-foreground h-3.5 w-3.5" />
                                                                                    </div>
                                                                                    <div className="min-w-0">
                                                                                        <div className="group-hover:text-primary flex items-center gap-1.5 leading-snug font-medium transition-colors">
                                                                                            {article.title}
                                                                                            {article.is_featured && (
                                                                                                <Star className="fill-warning text-warning h-3 w-3 shrink-0" />
                                                                                            )}
                                                                                            {article.is_restricted && (
                                                                                                <Lock className="text-muted-foreground h-3 w-3 shrink-0" />
                                                                                            )}
                                                                                        </div>
                                                                                        {article.category && (
                                                                                            <div className="text-muted-foreground mt-0.5 text-xs">
                                                                                                {article.category.name}
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                </Link>
                                                                            </td>
                                                                            <td className="px-4 py-3">
                                                                                <span
                                                                                    className={cn(
                                                                                        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                                                                                        STATUS_STYLES[article.status] ??
                                                                                            'bg-muted text-muted-foreground',
                                                                                    )}
                                                                                >
                                                                                    {t(`knowledge_base.status.${article.status}`, article.status)}
                                                                                </span>
                                                                            </td>
                                                                            <td className="hidden px-4 py-3 sm:table-cell">
                                                                                <div className="text-sm">{date}</div>
                                                                                {time && <div className="text-muted-foreground text-xs">{time}</div>}
                                                                            </td>
                                                                            <td className="px-4 py-3">
                                                                                {can('knowledge-base.update') && (
                                                                                    <DropdownMenu>
                                                                                        <DropdownMenuTrigger asChild>
                                                                                            <button
                                                                                                type="button"
                                                                                                className="text-muted-foreground hover:bg-accent hover:text-foreground flex h-7 w-7 items-center justify-center rounded opacity-0 transition-opacity group-hover:opacity-100"
                                                                                            >
                                                                                                <MoreHorizontal className="h-4 w-4" />
                                                                                            </button>
                                                                                        </DropdownMenuTrigger>
                                                                                        <DropdownMenuContent align="end">
                                                                                            <DropdownMenuItem asChild>
                                                                                                <Link href={route('knowledge-base.edit', article.id)}>
                                                                                                    <Edit className="h-4 w-4" />
                                                                                                    {t(
                                                                                                        'knowledge_base.index.article_actions.edit',
                                                                                                        'Edit article',
                                                                                                    )}
                                                                                                </Link>
                                                                                            </DropdownMenuItem>
                                                                                            <DropdownMenuSeparator />
                                                                                            <DropdownMenuItem
                                                                                                onClick={() => handleDeleteArticle(article.id)}
                                                                                                className="text-destructive focus:text-destructive"
                                                                                            >
                                                                                                <Trash2 className="h-4 w-4" />
                                                                                                {t(
                                                                                                    'knowledge_base.index.article_actions.delete',
                                                                                                    'Delete',
                                                                                                )}
                                                                                            </DropdownMenuItem>
                                                                                        </DropdownMenuContent>
                                                                                    </DropdownMenu>
                                                                                )}
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                        {articles.length > rowsPerPage && (
                                                            <div className="flex flex-wrap items-center justify-between gap-4 border-t px-4 py-3">
                                                                <span className="text-muted-foreground text-sm">
                                                                    {t(
                                                                        'knowledge_base.index.pagination.showing',
                                                                        'Showing :from–:to of :total articles',
                                                                    )
                                                                        .replace(':from', String(startRow))
                                                                        .replace(':to', String(endRow))
                                                                        .replace(':total', String(articles.length))}
                                                                </span>
                                                                <div className="flex items-center gap-4">
                                                                    {totalPages > 1 && (
                                                                        <div className="flex items-center gap-1">
                                                                            <button
                                                                                type="button"
                                                                                disabled={clampedPage === 1}
                                                                                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                                                                className="text-muted-foreground hover:bg-accent flex h-7 w-7 items-center justify-center rounded transition-colors disabled:opacity-40"
                                                                            >
                                                                                <ChevronLeft className="h-4 w-4" />
                                                                            </button>
                                                                            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map(
                                                                                (page) => (
                                                                                    <button
                                                                                        key={page}
                                                                                        type="button"
                                                                                        onClick={() => setCurrentPage(page)}
                                                                                        className={cn(
                                                                                            'flex h-7 w-7 items-center justify-center rounded text-sm transition-colors',
                                                                                            clampedPage === page
                                                                                                ? 'bg-primary text-primary-foreground font-medium'
                                                                                                : 'text-muted-foreground hover:bg-accent',
                                                                                        )}
                                                                                    >
                                                                                        {page}
                                                                                    </button>
                                                                                ),
                                                                            )}
                                                                            <button
                                                                                type="button"
                                                                                disabled={clampedPage === totalPages}
                                                                                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                                                                className="text-muted-foreground hover:bg-accent flex h-7 w-7 items-center justify-center rounded transition-colors disabled:opacity-40"
                                                                            >
                                                                                <ChevronRight className="h-4 w-4" />
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="text-muted-foreground text-xs">
                                                                            {t('knowledge_base.index.pagination.rows_per_page', 'Rows per page')}
                                                                        </span>
                                                                        <Select
                                                                            value={String(rowsPerPage)}
                                                                            onValueChange={(v) => {
                                                                                setRowsPerPage(Number(v));
                                                                                setCurrentPage(1);
                                                                            }}
                                                                        >
                                                                            <SelectTrigger className="h-7 w-16 text-xs">
                                                                                <SelectValue />
                                                                            </SelectTrigger>
                                                                            <SelectContent>
                                                                                {[8, 16, 32].map((n) => (
                                                                                    <SelectItem key={n} value={String(n)}>
                                                                                        {n}
                                                                                    </SelectItem>
                                                                                ))}
                                                                            </SelectContent>
                                                                        </Select>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="divide-y overflow-hidden rounded-lg border">
                                                    {articles.map((article) => (
                                                        <Link
                                                            key={article.id}
                                                            href={route('knowledge-base.show', { slug: article.slug, locale: article.locale })}
                                                            className="group hover:bg-accent/20 flex items-start gap-4 rounded-md px-4 py-4 transition-colors"
                                                        >
                                                            <div className="bg-muted mt-0.5 shrink-0 rounded-md p-2">
                                                                <FileText className="text-muted-foreground h-4 w-4" />
                                                            </div>
                                                            <div className="min-w-0 flex-1">
                                                                <div className="flex flex-wrap items-center gap-2">
                                                                    <span className="group-hover:text-primary leading-snug font-medium transition-colors">
                                                                        {article.title}
                                                                    </span>
                                                                    {article.is_featured && (
                                                                        <Star className="fill-warning text-warning h-3.5 w-3.5" />
                                                                    )}
                                                                    {article.is_restricted && <Lock className="text-muted-foreground h-3 w-3" />}
                                                                </div>
                                                                {article.summary && (
                                                                    <p className="text-muted-foreground mt-0.5 line-clamp-2 text-sm leading-5">
                                                                        {article.summary}
                                                                    </p>
                                                                )}
                                                                <div className="text-muted-foreground mt-1.5 flex flex-wrap items-center gap-2 text-xs">
                                                                    {article.category && (
                                                                        <span className="text-muted-foreground/80">{article.category.name}</span>
                                                                    )}
                                                                    {article.tags.slice(0, 3).map((tag) => (
                                                                        <span key={tag}>#{tag}</span>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                            <ChevronRight className="text-muted-foreground group-hover:text-primary mt-1 h-4 w-4 shrink-0 transition-colors" />
                                                        </Link>
                                                    ))}
                                                </div>
                                            )}
                                        </section>
                                    )}

                                    {/* Empty */}
                                    {articles.length === 0 && (
                                        <div className="flex flex-col items-center justify-center py-24 text-center">
                                            <BookOpenText className="text-muted-foreground/40 h-10 w-10" />
                                            <h2 className="mt-4 text-base font-semibold">
                                                {t('knowledge_base.index.empty.title', 'No articles yet')}
                                            </h2>
                                            {can('knowledge-base.create') && (
                                                <Button asChild size="sm" className="mt-4">
                                                    <Link href={route('knowledge-base.create')}>
                                                        <Plus className="h-4 w-4" />
                                                        {t('knowledge_base.index.actions.create', 'New Article')}
                                                    </Link>
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            /* ─── Category view ─────────────────────────── */
                            <div className="flex flex-1 flex-col overflow-hidden">
                                {/* Category header */}
                                <div className="border-b px-6 py-4">
                                    <div className="flex items-center justify-between gap-4">
                                        <div>
                                            <h2 className="text-lg font-semibold">
                                                {selectedCategorySlug === '__no_category__'
                                                    ? t('knowledge_base.index.no_category', 'No category')
                                                    : activeCategory?.name}
                                            </h2>
                                            {activeCategory?.description && (
                                                <p className="text-muted-foreground mt-0.5 text-sm">{activeCategory.description}</p>
                                            )}
                                        </div>
                                        <div className="flex shrink-0 items-center gap-2">
                                            {canManage && (
                                                <button
                                                    type="button"
                                                    onClick={() => setAdminMode((v) => !v)}
                                                    className={cn(
                                                        'flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs transition-colors',
                                                        adminMode ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:bg-accent',
                                                    )}
                                                >
                                                    <SlidersHorizontal className="h-3.5 w-3.5" />
                                                    {t('knowledge_base.index.admin_view', 'Manage')}
                                                </button>
                                            )}
                                            {can('knowledge-base.create') && (
                                                <Button asChild size="sm">
                                                    <Link href={route('knowledge-base.create')}>
                                                        <Plus className="h-4 w-4" />
                                                        {t('knowledge_base.index.actions.create', 'New Article')}
                                                    </Link>
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Admin tabs + toolbar */}
                                {canManage && adminMode && (
                                    <>
                                        <div className="border-b px-6">
                                            <div className="flex items-end justify-between">
                                                <div className="flex">
                                                    {STATUS_TABS.map((tab) => (
                                                        <button
                                                            key={tab.key}
                                                            type="button"
                                                            onClick={() => applyFilters({ status: tab.key || undefined })}
                                                            className={cn(
                                                                'border-b-2 px-4 py-3 text-sm font-medium transition-colors',
                                                                selectedStatus === tab.key
                                                                    ? 'border-primary text-primary'
                                                                    : 'text-muted-foreground hover:text-foreground border-transparent',
                                                            )}
                                                        >
                                                            {tab.label}
                                                        </button>
                                                    ))}
                                                </div>
                                                {statusCounts && Object.keys(statusCounts).length > 0 && (
                                                    <div className="text-muted-foreground flex items-center gap-4 pb-3 text-xs">
                                                        {(['published', 'draft', 'archived'] as const).map((s) =>
                                                            statusCounts[s] ? (
                                                                <span key={s} className="flex items-center gap-1.5">
                                                                    <span className={cn('h-1.5 w-1.5 rounded-full', STATUS_DOT[s])} />
                                                                    {t(`knowledge_base.status.${s}`, s)}{' '}
                                                                    <span className="text-foreground font-medium">{statusCounts[s]}</span>
                                                                </span>
                                                            ) : null,
                                                        )}
                                                        <span>
                                                            {t('knowledge_base.index.total_label', 'Total')}{' '}
                                                            <span className="text-foreground font-medium">{articles.length}</span>
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 border-b px-6 py-2">
                                            <Select value={selectedSort} onValueChange={(v) => applyFilters({ sort: v })}>
                                                <SelectTrigger className="h-7 w-48 text-xs">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="updated">
                                                        {t('knowledge_base.index.filters.sort_newest', 'Newest updated')}
                                                    </SelectItem>
                                                    <SelectItem value="oldest">{t('knowledge_base.index.filters.sort_oldest', 'Oldest')}</SelectItem>
                                                    <SelectItem value="title">{t('knowledge_base.index.filters.sort_title', 'Title A–Z')}</SelectItem>
                                                </SelectContent>
                                            </Select>
                                            <div className="flex items-center gap-0.5">
                                                <button
                                                    type="button"
                                                    onClick={() => setViewMode('list')}
                                                    className={cn(
                                                        'rounded p-1.5 transition-colors',
                                                        viewMode === 'list'
                                                            ? 'bg-accent text-accent-foreground'
                                                            : 'text-muted-foreground hover:bg-accent',
                                                    )}
                                                >
                                                    <List className="h-3.5 w-3.5" />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setViewMode('grid')}
                                                    className={cn(
                                                        'rounded p-1.5 transition-colors',
                                                        viewMode === 'grid'
                                                            ? 'bg-accent text-accent-foreground'
                                                            : 'text-muted-foreground hover:bg-accent',
                                                    )}
                                                >
                                                    <LayoutGrid className="h-3.5 w-3.5" />
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* Articles */}
                                <div className="flex-1 overflow-y-auto">
                                    {articles.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center py-24 text-center">
                                            <BookOpenText className="text-muted-foreground/40 h-10 w-10" />
                                            <h2 className="mt-4 text-base font-semibold">
                                                {t('knowledge_base.index.empty.title', 'No articles found')}
                                            </h2>
                                            <p className="text-muted-foreground mt-1 text-sm">
                                                {t('knowledge_base.index.empty.description', 'Try a different category or language.')}
                                            </p>
                                        </div>
                                    ) : canManage && adminMode ? (
                                        /* Admin table */
                                        <>
                                            <table className="w-full border-separate border-spacing-0 text-sm">
                                                <thead>
                                                    <tr className="bg-muted/30 [&>th]:border-b">
                                                        <th className="text-muted-foreground px-6 py-3 text-left text-xs font-medium">
                                                            {t('knowledge_base.index.table.title', 'Title')}
                                                        </th>
                                                        <th className="text-muted-foreground px-4 py-3 text-left text-xs font-medium">
                                                            {t('knowledge_base.index.table.status', 'Status')}
                                                        </th>
                                                        <th className="text-muted-foreground px-4 py-3 text-left text-xs font-medium">
                                                            {t('knowledge_base.index.table.updated', 'Updated')}
                                                        </th>
                                                        <th className="text-muted-foreground px-4 py-3 text-left text-xs font-medium">
                                                            {t('knowledge_base.index.table.author', 'Author')}
                                                        </th>
                                                        <th className="w-10 px-4 py-3" />
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y">
                                                    {paginatedArticles.map((article) => {
                                                        const { date, time } = formatUpdatedAt(article.updated_at);
                                                        return (
                                                            <tr
                                                                key={article.id}
                                                                className="group hover:bg-muted/20 transition-colors [&>td:first-child]:rounded-l-md [&>td:last-child]:rounded-r-md"
                                                            >
                                                                <td className="px-6 py-3">
                                                                    <Link
                                                                        href={route('knowledge-base.show', {
                                                                            slug: article.slug,
                                                                            locale: article.locale,
                                                                        })}
                                                                        className="flex items-start gap-3"
                                                                    >
                                                                        <div className="bg-muted mt-0.5 shrink-0 rounded p-1.5">
                                                                            <FileText className="text-muted-foreground h-3.5 w-3.5" />
                                                                        </div>
                                                                        <div className="min-w-0">
                                                                            <div className="group-hover:text-primary flex items-center gap-1.5 leading-snug font-medium transition-colors">
                                                                                {article.title}
                                                                                {article.is_featured && (
                                                                                    <Star className="fill-warning text-warning h-3 w-3 shrink-0" />
                                                                                )}
                                                                                {article.is_restricted && (
                                                                                    <Lock className="text-muted-foreground h-3 w-3 shrink-0" />
                                                                                )}
                                                                            </div>
                                                                            {article.tags.length > 0 && (
                                                                                <div className="text-muted-foreground mt-0.5 text-xs">
                                                                                    {article.tags.slice(0, 3).join(', ')}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </Link>
                                                                </td>
                                                                <td className="px-4 py-3">
                                                                    <span
                                                                        className={cn(
                                                                            'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                                                                            STATUS_STYLES[article.status] ?? 'bg-muted text-muted-foreground',
                                                                        )}
                                                                    >
                                                                        {t(`knowledge_base.status.${article.status}`, article.status)}
                                                                    </span>
                                                                </td>
                                                                <td className="px-4 py-3">
                                                                    <div className="text-sm">{date}</div>
                                                                    {time && <div className="text-muted-foreground text-xs">{time}</div>}
                                                                </td>
                                                                <td className="px-4 py-3">
                                                                    {article.author ? (
                                                                        <div className="flex items-center gap-2">
                                                                            <div className="bg-primary/10 text-primary flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold">
                                                                                {getInitials(article.author.name)}
                                                                            </div>
                                                                            <span className="text-sm whitespace-nowrap">{article.author.name}</span>
                                                                        </div>
                                                                    ) : (
                                                                        <span className="text-muted-foreground">—</span>
                                                                    )}
                                                                </td>
                                                                <td className="px-4 py-3">
                                                                    {can('knowledge-base.update') && (
                                                                        <DropdownMenu>
                                                                            <DropdownMenuTrigger asChild>
                                                                                <button
                                                                                    type="button"
                                                                                    className="text-muted-foreground hover:bg-accent hover:text-foreground flex h-7 w-7 items-center justify-center rounded opacity-0 transition-opacity group-hover:opacity-100"
                                                                                >
                                                                                    <MoreHorizontal className="h-4 w-4" />
                                                                                </button>
                                                                            </DropdownMenuTrigger>
                                                                            <DropdownMenuContent align="end">
                                                                                <DropdownMenuItem asChild>
                                                                                    <Link href={route('knowledge-base.edit', article.id)}>
                                                                                        <Edit className="h-4 w-4" />
                                                                                        {t(
                                                                                            'knowledge_base.index.article_actions.edit',
                                                                                            'Edit article',
                                                                                        )}
                                                                                    </Link>
                                                                                </DropdownMenuItem>
                                                                                <DropdownMenuSeparator />
                                                                                <DropdownMenuItem
                                                                                    onClick={() => handleDeleteArticle(article.id)}
                                                                                    className="text-destructive focus:text-destructive"
                                                                                >
                                                                                    <Trash2 className="h-4 w-4" />
                                                                                    {t('knowledge_base.index.article_actions.delete', 'Delete')}
                                                                                </DropdownMenuItem>
                                                                            </DropdownMenuContent>
                                                                        </DropdownMenu>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                            {/* Pagination */}
                                            {articles.length > rowsPerPage && (
                                                <div className="flex flex-wrap items-center justify-between gap-4 border-t px-6 py-3">
                                                    <span className="text-muted-foreground text-sm">
                                                        {t('knowledge_base.index.pagination.showing', 'Showing :from–:to of :total articles')
                                                            .replace(':from', String(startRow))
                                                            .replace(':to', String(endRow))
                                                            .replace(':total', String(articles.length))}
                                                    </span>
                                                    <div className="flex items-center gap-4">
                                                        {totalPages > 1 && (
                                                            <div className="flex items-center gap-1">
                                                                <button
                                                                    type="button"
                                                                    disabled={clampedPage === 1}
                                                                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                                                    className="text-muted-foreground hover:bg-accent flex h-7 w-7 items-center justify-center rounded transition-colors disabled:opacity-40"
                                                                >
                                                                    <ChevronLeft className="h-4 w-4" />
                                                                </button>
                                                                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map((page) => (
                                                                    <button
                                                                        key={page}
                                                                        type="button"
                                                                        onClick={() => setCurrentPage(page)}
                                                                        className={cn(
                                                                            'flex h-7 w-7 items-center justify-center rounded text-sm transition-colors',
                                                                            clampedPage === page
                                                                                ? 'bg-primary text-primary-foreground font-medium'
                                                                                : 'text-muted-foreground hover:bg-accent',
                                                                        )}
                                                                    >
                                                                        {page}
                                                                    </button>
                                                                ))}
                                                                <button
                                                                    type="button"
                                                                    disabled={clampedPage === totalPages}
                                                                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                                                    className="text-muted-foreground hover:bg-accent flex h-7 w-7 items-center justify-center rounded transition-colors disabled:opacity-40"
                                                                >
                                                                    <ChevronRight className="h-4 w-4" />
                                                                </button>
                                                            </div>
                                                        )}
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-muted-foreground text-xs">
                                                                {t('knowledge_base.index.pagination.rows_per_page', 'Rows per page')}
                                                            </span>
                                                            <Select
                                                                value={String(rowsPerPage)}
                                                                onValueChange={(v) => {
                                                                    setRowsPerPage(Number(v));
                                                                    setCurrentPage(1);
                                                                }}
                                                            >
                                                                <SelectTrigger className="h-7 w-16 text-xs">
                                                                    <SelectValue />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    {[8, 16, 32].map((n) => (
                                                                        <SelectItem key={n} value={String(n)}>
                                                                            {n}
                                                                        </SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        /* User-friendly article list */
                                        <div className="divide-y">
                                            {articles.map((article) => (
                                                <Link
                                                    key={article.id}
                                                    href={route('knowledge-base.show', { slug: article.slug, locale: article.locale })}
                                                    className="group hover:bg-accent/20 flex items-start gap-4 rounded-md px-6 py-4 transition-colors"
                                                >
                                                    <div className="bg-muted mt-0.5 shrink-0 rounded-md p-2">
                                                        <FileText className="text-muted-foreground h-4 w-4" />
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex flex-wrap items-center gap-2">
                                                            <span className="group-hover:text-primary leading-snug font-medium transition-colors">
                                                                {article.title}
                                                            </span>
                                                            {article.is_featured && <Star className="fill-warning text-warning h-3.5 w-3.5" />}
                                                            {article.is_restricted && <Lock className="text-muted-foreground h-3 w-3" />}
                                                            {article.status !== 'published' && (
                                                                <span
                                                                    className={cn(
                                                                        'rounded px-1.5 py-0.5 text-[11px] font-medium',
                                                                        STATUS_STYLES[article.status] ?? 'bg-muted text-muted-foreground',
                                                                    )}
                                                                >
                                                                    {t(`knowledge_base.status.${article.status}`, article.status)}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {article.summary && (
                                                            <p className="text-muted-foreground mt-0.5 line-clamp-2 text-sm leading-5">
                                                                {article.summary}
                                                            </p>
                                                        )}
                                                        <div className="text-muted-foreground mt-1.5 flex flex-wrap items-center gap-2 text-xs">
                                                            {article.tags.slice(0, 3).map((tag) => (
                                                                <span key={tag}>#{tag}</span>
                                                            ))}
                                                            {article.updated_at && (
                                                                <span className="text-muted-foreground/60">
                                                                    {new Date(article.updated_at).toLocaleDateString('en', {
                                                                        month: 'short',
                                                                        day: 'numeric',
                                                                    })}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <ChevronRight className="text-muted-foreground group-hover:text-primary mt-1 h-4 w-4 shrink-0 transition-colors" />
                                                </Link>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </AppLayout>
    );
}
